import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Loader2, Calendar, Clock, TrendingUp } from 'lucide-react';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const News = () => {
  const { language } = useLanguage();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    fetchNewsFeeds();
  }, [language]);

  const openArticleModal = (article) => {
    setSelectedArticle(article);
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden'; // Prevent background scrolling
  };

  const closeArticleModal = () => {
    setIsModalOpen(false);
    setSelectedArticle(null);
    document.body.style.overflow = 'auto'; // Restore scrolling
  };

  useEffect(() => {
    fetchNewsFeeds();
  }, [language]);

  const fetchNewsFeeds = async () => {
    try {
      const response = await axios.get(`${API}/news/feeds`);
      if (response.data && response.data.length > 0) {
        const formattedNews = response.data.map(item => ({
          ...item,
          title: language === 'fr' ? item.title : (item.title_en || item.title),
          content: language === 'fr' ? item.content : (item.content_en || item.content)
        }));
        setNews(formattedNews);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching news feeds:', error);
      setLoading(false);
    }
  };

  const categories = [
    { value: 'all', label: language === 'fr' ? 'Toutes' : 'All' },
    { value: 'economy', label: language === 'fr' ? 'Économie' : 'Economy' },
    { value: 'mining', label: language === 'fr' ? 'Mines' : 'Mining' },
    { value: 'technology', label: language === 'fr' ? 'Technologie' : 'Technology' },
    { value: 'infrastructure', label: language === 'fr' ? 'Infrastructure' : 'Infrastructure' },
    { value: 'transport', label: language === 'fr' ? 'Transport' : 'Transport' },
    { value: 'energy', label: language === 'fr' ? 'Énergie' : 'Energy' },
    { value: 'agriculture', label: language === 'fr' ? 'Agriculture' : 'Agriculture' },
    { value: 'education', label: language === 'fr' ? 'Éducation' : 'Education' },
    { value: 'cooperation', label: language === 'fr' ? 'Coopération' : 'Cooperation' }
  ];

  // Premium Advertisers
  const advertisers = [
    {
      name: 'Hugo School',
      slogan: language === 'fr' ? 'Excellence en Éducation' : 'Excellence in Education',
      description: language === 'fr' 
        ? 'Formation de qualité pour les leaders de demain. Programmes scolaires modernes et infrastructure de pointe.'
        : 'Quality education for tomorrow\'s leaders. Modern school programs and state-of-the-art infrastructure.',
      url: 'https://hugoschool.cd',
      website: 'hugoschool.cd',
      color: 'from-blue-600 to-blue-800',
      icon: '🎓'
    },
    {
      name: 'Computer Based Technology',
      slogan: language === 'fr' ? 'Votre Partenaire Tech' : 'Your Tech Partner',
      description: language === 'fr'
        ? 'Solutions technologiques innovantes pour entreprises. Formation professionnelle en informatique et développement.'
        : 'Innovative technology solutions for businesses. Professional training in IT and development.',
      url: 'https://cbtkomba.cd',
      website: 'cbtkomba.cd',
      color: 'from-purple-600 to-purple-800',
      icon: '💻'
    }
  ];

  const filteredNews = selectedCategory === 'all' 
    ? news 
    : news.filter(article => article.category === selectedCategory);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getReadTime = (content) => {
    const wordsPerMinute = 200;
    const words = content.split(' ').length;
    const minutes = Math.ceil(words / wordsPerMinute);
    return `${minutes} ${language === 'fr' ? 'min' : 'min'}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-primary animate-spin mx-auto mb-4" />
          <p className="text-gray-600">{language === 'fr' ? 'Chargement...' : 'Loading...'}</p>
        </div>
      </div>
    );
  }

  const heroArticle = filteredNews[0];
  const remainingArticles = filteredNews.slice(1);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Article Modal */}
      {isModalOpen && selectedArticle && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/70 backdrop-blur-sm transition-opacity"
            onClick={closeArticleModal}
          ></div>
          
          {/* Modal Content */}
          <div className="relative min-h-screen flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
            >
              {/* Close Button */}
              <button
                onClick={closeArticleModal}
                className="absolute top-4 right-4 z-10 bg-white/90 backdrop-blur-sm p-2 rounded-full hover:bg-white transition-all shadow-lg"
              >
                <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Scrollable Content */}
              <div className="overflow-y-auto max-h-[90vh]">
                {/* Hero Image */}
                <div className="relative h-96 overflow-hidden">
                  <img
                    src={selectedArticle.image || 'https://images.unsplash.com/photo-1633960719106-894ffd8b34d2?crop=entropy&cs=srgb&fm=jpg&q=85'}
                    alt={selectedArticle.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                  
                  {/* Title Overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                    <span className="inline-block px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-semibold uppercase mb-4">
                      {selectedArticle.category}
                    </span>
                    <h1 className="font-serif text-4xl font-bold mb-4 leading-tight">
                      {selectedArticle.title}
                    </h1>
                  </div>
                </div>

                {/* Article Content */}
                <div className="p-8 lg:p-12">
                  {/* Meta Information */}
                  <div className="flex items-center space-x-6 pb-6 mb-6 border-b border-gray-200">
                    <div className="flex items-center text-gray-600">
                      <Calendar className="h-5 w-5 mr-2 text-primary" />
                      <span className="font-medium">{formatDate(selectedArticle.published_date)}</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <Clock className="h-5 w-5 mr-2 text-primary" />
                      <span className="font-medium">{getReadTime(selectedArticle.content)} {language === 'fr' ? 'de lecture' : 'read'}</span>
                    </div>
                    <div className="flex items-center text-primary font-semibold">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                      </svg>
                      {selectedArticle.source}
                    </div>
                  </div>

                  {/* Article Body */}
                  <div className="prose prose-lg max-w-none">
                    <p className="text-gray-700 leading-relaxed text-lg whitespace-pre-line">
                      {selectedArticle.content}
                    </p>
                  </div>

                  {/* Share Section */}
                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <h3 className="font-serif text-xl font-bold mb-4">
                      {language === 'fr' ? 'Partager cet article' : 'Share this article'}
                    </h3>
                    <div className="flex space-x-3">
                      <button className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                        </svg>
                        <span className="text-sm font-semibold">Facebook</span>
                      </button>
                      <button className="flex items-center space-x-2 px-4 py-2 bg-blue-400 text-white rounded-lg hover:bg-blue-500 transition-colors">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                        </svg>
                        <span className="text-sm font-semibold">Twitter</span>
                      </button>
                      <button className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                        </svg>
                        <span className="text-sm font-semibold">WhatsApp</span>
                      </button>
                    </div>
                  </div>

                  {/* Source Link */}
                  {selectedArticle.url && (
                    <div className="mt-6">
                      <a
                        href={selectedArticle.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-primary hover:text-primary/80 font-semibold"
                      >
                        <span>{language === 'fr' ? 'Voir la source originale' : 'View original source'}</span>
                        <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      )}

      {/* Hero Section with breaking news badge */}
      <div className="bg-gradient-to-b from-primary to-primary/95 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center space-x-3 mb-6"
          >
            <TrendingUp className="h-8 w-8 text-secondary" />
            <h1 className="font-serif text-4xl sm:text-5xl font-bold">
              {language === 'fr' ? 'Actualités RDC & Congo' : 'DRC & Congo News'}
            </h1>
          </motion.div>
          <p className="text-xl opacity-90 max-w-2xl">
            {language === 'fr' 
              ? 'Les dernières actualités économiques et politiques de la République Démocratique du Congo et du Congo Brazzaville'
              : 'Latest economic and political news from the Democratic Republic of Congo and Congo Brazzaville'}
          </p>
        </div>
      </div>

      {/* Category Filter */}
      <div className="bg-white border-b border-gray-200 sticky top-20 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex overflow-x-auto py-4 space-x-2 scrollbar-hide">
            {categories.map((category) => (
              <button
                key={category.value}
                onClick={() => setSelectedCategory(category.value)}
                className={`px-6 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-300 ${
                  selectedCategory === category.value
                    ? 'bg-primary text-white shadow-md scale-105'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {filteredNews.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg">
              {language === 'fr' ? 'Aucune actualité disponible pour cette catégorie.' : 'No news available for this category.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Main Content Area */}
            <div className="lg:col-span-8">
              {/* Hero Article */}
              {heroArticle && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-12"
                >
                  <div className="relative bg-white rounded-2xl overflow-hidden shadow-2xl group">
                    {/* Featured badge */}
                    <div className="absolute top-6 left-6 z-10">
                      <span className="bg-secondary text-white px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wider shadow-lg">
                        {language === 'fr' ? 'À la Une' : 'Featured'}
                      </span>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-0">
                      {/* Image */}
                      <div className="relative h-96 md:h-auto overflow-hidden">
                        <img
                          src={heroArticle.image || 'https://images.unsplash.com/photo-1633960719106-894ffd8b34d2?crop=entropy&cs=srgb&fm=jpg&q=85'}
                          alt={heroArticle.title}
                          className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                      </div>
                      
                      {/* Content */}
                      <div className="p-8 lg:p-10 flex flex-col justify-center">
                        <div className="flex items-center space-x-4 mb-4">
                          <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-xs font-semibold uppercase">
                            {heroArticle.category}
                          </span>
                          <div className="flex items-center text-gray-500 text-sm">
                            <Calendar className="h-4 w-4 mr-1" />
                            {formatDate(heroArticle.published_date)}
                          </div>
                        </div>
                        
                        <h2 className="font-serif text-2xl lg:text-3xl font-bold text-gray-900 mb-4 leading-tight">
                          {heroArticle.title}
                        </h2>
                        
                        <p className="text-gray-600 text-base leading-relaxed mb-6 line-clamp-4">
                          {heroArticle.content}
                        </p>
                        
                        <button
                          onClick={() => openArticleModal(heroArticle)}
                          className="inline-flex items-center px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:scale-105"
                        >
                          {language === 'fr' ? 'Lire l\'Article Complet' : 'Read Full Article'}
                          <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                        
                        <div className="flex items-center justify-between mt-6">
                          <div className="flex items-center space-x-4 text-sm text-gray-500">
                            <div className="flex items-center">
                              <Clock className="h-4 w-4 mr-1" />
                              {getReadTime(heroArticle.content)}
                            </div>
                            <span className="font-medium text-primary">{heroArticle.source}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Grid of Articles */}
              <div className="grid md:grid-cols-2 gap-8">
                {remainingArticles.map((article, index) => (
                  <motion.article
                    key={article.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group"
                  >
                    {/* Image */}
                    <div className="relative h-56 overflow-hidden">
                      <img
                        src={article.image || 'https://images.unsplash.com/photo-1633960719106-894ffd8b34d2?crop=entropy&cs=srgb&fm=jpg&q=85'}
                        alt={article.title}
                        className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                      <span className="absolute top-4 left-4 px-3 py-1 bg-white/90 backdrop-blur-sm text-primary rounded-full text-xs font-semibold uppercase">
                        {article.category}
                      </span>
                    </div>
                    
                    {/* Content */}
                    <div className="p-6">
                      <div className="flex items-center space-x-3 text-sm text-gray-500 mb-3">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1" />
                          {formatDate(article.published_date)}
                        </div>
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 mr-1" />
                          {getReadTime(article.content)}
                        </div>
                      </div>
                      
                      <h3 className="font-serif text-xl font-bold text-gray-900 mb-3 leading-tight group-hover:text-primary transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      
                      <p className="text-gray-600 leading-relaxed mb-4 line-clamp-3">
                        {article.content}
                      </p>
                      
                      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                        <span className="text-sm font-medium text-primary">{article.source}</span>
                        <button 
                          onClick={() => openArticleModal(article)}
                          className="text-primary hover:text-primary/80 text-sm font-semibold flex items-center group/btn"
                        >
                          {language === 'fr' ? 'Lire Plus' : 'Read More'}
                          <svg className="w-4 h-4 ml-1 transform group-hover/btn:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </motion.article>
                ))}
              </div>
            </div>

            {/* Premium Advertising Sidebar */}
            <div className="lg:col-span-4">
              <div className="sticky top-32 space-y-6">
                {/* Sidebar Header */}
                <div className="bg-gradient-to-r from-primary to-primary/90 text-white p-4 rounded-xl">
                  <h3 className="font-serif text-xl font-bold text-center">
                    {language === 'fr' ? 'Nos Partenaires' : 'Our Partners'}
                  </h3>
                </div>

                {/* Advertisers */}
                {advertisers.map((advertiser, index) => (
                  <motion.a
                    key={index}
                    href={advertiser.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.2 }}
                    viewport={{ once: true }}
                    className="block group"
                  >
                    <div className={`relative bg-gradient-to-br ${advertiser.color} text-white rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105`}>
                      {/* Decorative pattern */}
                      <div className="absolute inset-0 opacity-10">
                        <div className="absolute inset-0" style={{
                          backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
                          backgroundSize: '20px 20px'
                        }}></div>
                      </div>
                      
                      <div className="relative p-6">
                        {/* Icon */}
                        <div className="text-5xl mb-4 transform group-hover:scale-110 transition-transform">
                          {advertiser.icon}
                        </div>
                        
                        {/* Content */}
                        <h4 className="font-serif text-2xl font-bold mb-2">
                          {advertiser.name}
                        </h4>
                        <p className="text-sm font-semibold uppercase tracking-wider mb-3 opacity-90">
                          {advertiser.slogan}
                        </p>
                        <p className="text-sm leading-relaxed mb-4 opacity-90">
                          {advertiser.description}
                        </p>
                        
                        {/* CTA */}
                        <div className="flex items-center justify-between pt-4 border-t border-white/20">
                          <span className="text-sm font-medium opacity-75">{advertiser.website}</span>
                          <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-semibold flex items-center group-hover:bg-white/30 transition-all">
                            {language === 'fr' ? 'Visiter' : 'Visit'}
                            <svg className="w-4 h-4 ml-1 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </div>
                        </div>
                      </div>
                      
                      {/* Corner badge */}
                      <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-bold uppercase">
                        {language === 'fr' ? 'Partenaire' : 'Partner'}
                      </div>
                    </div>
                  </motion.a>
                ))}

                {/* Info box */}
                <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-primary/10">
                  <h4 className="font-serif text-lg font-bold text-gray-900 mb-3">
                    {language === 'fr' ? '📢 Espace Publicitaire' : '📢 Advertising Space'}
                  </h4>
                  <p className="text-sm text-gray-600 mb-4">
                    {language === 'fr' 
                      ? 'Vous souhaitez promouvoir votre entreprise auprès de notre audience ? Contactez-nous pour plus d\'informations.'
                      : 'Want to promote your business to our audience? Contact us for more information.'}
                  </p>
                  <a href="/contact" className="inline-block bg-primary text-white px-6 py-2 rounded-lg text-sm font-semibold hover:bg-primary/90 transition-colors">
                    {language === 'fr' ? 'Nous Contacter' : 'Contact Us'}
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default News;
